import { useState } from 'react'
import { getAIProviderOption } from '@creanizer/types'
import { indexedDBManager } from '@creanizer/utils'
import { testProviderConnection } from '../../services/providerApi'
import { useSettingsStore } from '../../stores/settingsStore'
import { AIProviderSettings } from './AIProviderSettings'
import { ApiKeyManager } from './ApiKeyManager'

interface ApiKeyModalProps {
  isOpen: boolean
  onClose: () => void
}

export function ApiKeyModal({ isOpen, onClose }: ApiKeyModalProps) {
  const {
    activeApiKey,
    selectedProvider,
    addApiKey,
    getApiKeysForProvider,
  } = useSettingsStore()
  const [apiKey, setApiKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const [showManager, setShowManager] = useState(false)
  const [testStatus, setTestStatus] = useState<string | null>(null)
  const provider = getAIProviderOption(selectedProvider)
  const apiKeys = getApiKeysForProvider()

  if (!isOpen) return null
  if (showManager) {
    return (
      <ApiKeyManager
        isOpen
        onClose={() => {
          setShowManager(false)
          onClose()
        }}
      />
    )
  }

  const saveKey = async () => {
    if (!apiKey.trim()) return
    try {
      await addApiKey(
        `${provider.label}キー #${apiKeys.length + 1}`,
        apiKey.trim(),
        selectedProvider
      )
      setApiKey('')
      setTestStatus('APIキーを保存しました')
    } catch (error) {
      setTestStatus(error instanceof Error ? error.message : '保存に失敗しました')
    }
  }

  const testConnection = async () => {
    const key = apiKey.trim() || activeApiKey?.key
    if (!key) {
      setTestStatus('先にAPIキーを入力または登録してください')
      return
    }
    setTestStatus('接続を確認しています')
    try {
      await testProviderConnection(selectedProvider, key)
      setTestStatus('接続に成功しました')
    } catch (error) {
      setTestStatus(error instanceof Error ? error.message : '接続に失敗しました')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6">
        <h2 className="mb-4 text-xl font-semibold">AI接続設定</h2>
        <div className="space-y-4">
          <AIProviderSettings />
          <div className={`rounded border p-3 ${
            activeApiKey ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
          }`}>
            {activeApiKey
              ? `使用中: ${activeApiKey.name}`
              : `${provider.label}のAPIキーが設定されていません`}
          </div>
          {apiKeys.length > 0 && (
            <button
              className="btn btn-secondary w-full"
              onClick={() => setShowManager(true)}
            >
              APIキーを管理（{apiKeys.length}個）
            </button>
          )}
          <div className="border-t pt-4">
            <p className="mb-3 text-sm text-gray-600">
              新しいキーを追加:
              <a
                href={provider.apiKeyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 text-primary-600 hover:underline"
              >
                {provider.label}のキー取得ページ
              </a>
            </p>
            <div className="flex gap-2">
              <input
                type={showKey ? 'text' : 'password'}
                className="input flex-1"
                value={apiKey}
                onChange={event => setApiKey(event.target.value)}
                placeholder={provider.apiKeyPlaceholder}
              />
              <button className="btn btn-secondary" onClick={() => setShowKey(!showKey)}>
                {showKey ? '隠す' : '表示'}
              </button>
            </div>
          </div>
          {testStatus && <p className="text-sm text-gray-700">{testStatus}</p>}
          <p className="text-xs text-gray-600">
            保存先: {indexedDBManager.isUsingFallback() ? 'ブラウザー代替保存' : 'IndexedDB'}
          </p>
          <div className="flex flex-wrap justify-end gap-3">
            <button className="btn-outline px-4 py-2" onClick={onClose}>閉じる</button>
            <button className="btn btn-secondary" onClick={() => void testConnection()}>
              接続テスト
            </button>
            {apiKey.trim() && (
              <button className="btn-primary px-4 py-2" onClick={() => void saveKey()}>
                追加
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
